import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  App,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import {
  goodsReceiptNoteService,
  type GRNHeaderDTO,
  type GRNFilters,
} from '@/services/goodsReceiptNote.service';
import { useAiListSelection } from '@/hooks/useAiListSelection';

const { Title } = Typography;
const { Search } = Input;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

export const GoodsReceiptNotesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [receiptTypeFilter, setReceiptTypeFilter] = useState<string | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined);
  const [dateTo, setDateTo] = useState<string | undefined>(undefined);

  const filters: GRNFilters = {
    search: searchText || undefined,
    status: statusFilter,
    receipt_type: receiptTypeFilter,
    date_from: dateFrom,
    date_to: dateTo,
  };

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['goods-receipt-notes', filters],
    queryFn: () => goodsReceiptNoteService.list(filters),
  });
  const { rowSelection } = useAiListSelection({
    entityType: 'GoodsReceiptNote',
    module: 'procurement',
    listFilters: filters as Record<string, unknown>,
    labelById: (id) => list.find((o) => o.id === id)?.grn_number,
  });

  const handleSubmit = async (id: string) => {
    try {
      await goodsReceiptNoteService.submit(id);
      message.success('GRN submitted');
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-notes'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to submit GRN');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await goodsReceiptNoteService.approve(id);
      message.success('GRN approved');
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-notes'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to approve GRN');
    }
  };

  const handlePost = async (id: string) => {
    try {
      await goodsReceiptNoteService.post(id);
      message.success('GRN posted');
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-notes'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to post GRN');
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await goodsReceiptNoteService.cancel(id);
      message.success('GRN cancelled');
      queryClient.invalidateQueries({ queryKey: ['goods-receipt-notes'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to cancel GRN');
    }
  };

  const columns: ColumnsType<GRNHeaderDTO> = [
    {
      title: 'GRN #',
      dataIndex: 'grn_number',
      key: 'grn_number',
      width: 200,
      fixed: 'left' as const,
      ellipsis: true,
      onCell: () => ({ style: { overflow: 'hidden' } }),
      render: (val, record) => (
        <Button
          type="link"
          size="small"
          style={{ paddingInline: 0, maxWidth: '100%' }}
          onClick={() => navigate(`/scm/procurement/grn/${record.id}`)}
        >
          {val}
        </Button>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'grn_date',
      key: 'grn_date',
      width: 120,
      render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Type',
      dataIndex: 'receipt_type',
      key: 'receipt_type',
      width: 90,
    },
    {
      title: 'PO #',
      key: 'po_number',
      width: 130,
      ellipsis: true,
      render: (_, r) => (
        <DocumentRefLink
          to={r.po_id ? `/scm/procurement/purchase-orders/${r.po_id}` : undefined}
          number={r.purchaseOrder?.po_number}
          id={r.po_id}
        />
      ),
    },
    {
      title: 'Supplier',
      key: 'supplier',
      width: 200,
      ellipsis: true,
      render: (_, r) => r.supplier?.supplier_name ?? r.supplier_id ?? '—',
    },
    {
      title: 'Warehouse',
      key: 'warehouse',
      width: 140,
      ellipsis: true,
      render: (_, r) => r.warehouse?.warehouse_name ?? r.warehouse_location ?? '—',
    },
    {
      title: 'Status',
      dataIndex: 'grn_status',
      key: 'grn_status',
      width: 100,
      render: (s: string) => <Tag color={statusColors[s] ?? 'default'}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scm/procurement/grn/${record.id}`)}>
            View
          </Button>
          {record.grn_status === 'Draft' && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/scm/procurement/grn/${record.id}/edit`)}
            >
              Edit
            </Button>
          )}
          {record.grn_status === 'Draft' && (
            <Button type="link" size="small" onClick={() => handleSubmit(record.id)}>
              Submit
            </Button>
          )}
          {record.grn_status === 'Submitted' && (
            <Button type="link" size="small" onClick={() => handleApprove(record.id)}>
              Approve
            </Button>
          )}
          {(record.grn_status === 'Submitted' || record.grn_status === 'Approved') && (
            <Button type="link" size="small" onClick={() => handlePost(record.id)}>
              Post
            </Button>
          )}
          {!['Posted', 'Cancelled'].includes(record.grn_status) && (
            <Button type="link" size="small" danger onClick={() => handleCancel(record.id)}>
              Cancel
            </Button>
          )}
          <Button type="link" size="small" icon={<PrinterOutlined />} onClick={() => navigate(`/scm/procurement/grn/${record.id}`)}>
            Print
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0 }}>
            Goods Receipt Notes
          </Title>
          <p style={{ margin: '4px 0 0', color: '#666' }}>
            Create and manage material goods receipts against Purchase Orders
          </p>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="GRN register"
            description="Search by GRN #, PO #, supplier, or date. Filter by status and date range. Open GRN # for full workspace. Draft rows: Submit → Approve → Post from list or detail."
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/grn/new')}>
            Create GRN
          </Button>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Search
                placeholder="GRN #, PO #, supplier, date"
                allowClear
                onSearch={setSearchText}
                onChange={(e) => setSearchText(e.target.value)}
                value={searchText}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Submitted', label: 'Submitted' },
                  { value: 'Approved', label: 'Approved' },
                  { value: 'Posted', label: 'Posted' },
                  { value: 'Cancelled', label: 'Cancelled' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Receipt type"
                allowClear
                style={{ width: '100%' }}
                value={receiptTypeFilter}
                onChange={setReceiptTypeFilter}
                options={[
                  { value: 'Material', label: 'Material' },
                  { value: 'Service', label: 'Service' },
                  { value: 'Mixed', label: 'Mixed' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Input
                type="date"
                placeholder="From date"
                value={dateFrom ?? ''}
                onChange={(e) => setDateFrom(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Input
                type="date"
                placeholder="To date"
                value={dateTo ?? ''}
                onChange={(e) => setDateTo(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          <Table
            loading={isLoading}
            columns={columns}
            dataSource={list.map((o) => ({ ...o, key: o.id }))}
            rowKey="id"
            rowSelection={rowSelection}
            sticky={{ offsetHeader: 0 }}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (t) => `Total ${t} GRNs` }}
            size="small"
            scroll={{ x: 1200, y: 480 }}
          />
        </Space>
      </Card>
    </div>
  );
};
